import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { LayoutGrid } from 'lucide-react'
import { cn } from '@/lib/utils'
import { chipClass } from './FeatureFilters'
import RailArrow from './RailArrow'
import type { ServiceFacet } from '@/types/publicSchool'

interface ServiceFiltersProps {
  /** Os serviços do catálogo, já ordenados pelo servidor. */
  facets: ServiceFacet[]
  selected: string | null
  /** `null` é o "Todos" — sem recorte. */
  onChange: (service: string | null) => void
  className?: string
}

/**
 * O filtro do Explore: os serviços numa fila de chips, logo abaixo da linha da
 * marca.
 *
 * Chip de texto, não card com foto. A versão com card ocupava três vezes a
 * altura e empurrava a lista e o mapa para fora da primeira tela — num filtro
 * de mais de trinta itens, o que importa é ler os nomes rápido. As fotos do
 * catálogo continuam onde ajudam: no wizard, onde a pessoa escolhe o que o
 * negócio dela faz e olha um item de cada vez.
 *
 * Um nível só, de propósito: a fila mostra os serviços diretamente, sem pedir a
 * categoria antes. As categorias organizam a ORDEM (Esportes primeiro), mas não
 * são um passo da navegação.
 *
 * Não confundir com os SELOS (aula para iniciante, Wi-Fi, estacionamento), que
 * ficam no dialog do botão "Filtros" ao lado: aqueles são atributos de um
 * negócio, estes são o que ele faz.
 *
 * Escolha única: quem busca procura uma coisa de cada vez — daí `role="radio"`
 * e não `checkbox` como nos selos.
 */
export default function ServiceFilters({
  facets,
  selected,
  onChange,
  className,
}: ServiceFiltersProps) {
  return (
    <div className={cn('min-w-0 flex-1', className)}>
      <ChipTrack
        id="filtro-servicos"
        label="Filtrar por serviço"
        noun="serviços"
        // A ordem da fila muda com as contagens (ativos primeiro), então a
        // assinatura precisa das keys na ordem, não só do serviço escolhido.
        resetKey={`${selected ?? ''}|${facets.map((f) => f.key).join(',')}`}
      >
        {/* "Todos" não é um serviço: é a ausência de recorte. Fica sempre no
            início e nunca desabilitado — é a saída de quem se prendeu num
            serviço vazio. */}
        <button
          type="button"
          role="radio"
          aria-checked={selected === null}
          onClick={() => onChange(null)}
          className={chipClass(selected === null, false)}
        >
          <LayoutGrid className="h-4 w-4" aria-hidden="true" />
          Todos
        </button>

        {facets.map(({ key, label, count }) => {
          const active = selected === key
          // Serviço que ninguém oferece fica visível mas travado: clicar levaria
          // a uma lista vazia garantida. Visível, e não escondido, para a fila
          // não dançar a cada troca de cidade ou de área do mapa.
          const disabled = count === 0 && !active

          return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={disabled}
              onClick={() => onChange(active ? null : key)}
              className={chipClass(active, disabled)}
            >
              {label}
            </button>
          )
        })}
      </ChipTrack>
    </div>
  )
}

/**
 * A fila rolável.
 *
 * A mecânica (converter roda vertical em rolagem horizontal, esconder a barra,
 * acender as setas nas pontas) é sutil o bastante para viver num lugar só — com
 * mais de trinta chips, rolar é o comportamento principal da fila.
 */
function ChipTrack({
  id,
  label,
  noun,
  resetKey,
  children,
}: {
  /** Id do scroller, para o `aria-controls` das setas apontarem para ele. */
  id: string
  label: string
  /** Ver `useRailScroll`: muda, o trilho volta ao começo. */
  resetKey: string
  /** Entra nos `aria-label` das setas: "Ver mais serviços". */
  noun: string
  children: ReactNode
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)

  /**
   * Volta ao primeiro chip quando a fila muda de conteúdo.
   *
   * Os filtros navegam com `preserveState: true` (Explore.tsx), então trocar de
   * serviço ou de cidade troca as props SEM remontar este componente — e a fila
   * ficava parada no scroll de antes, abrindo no meio da lista como se já
   * tivesse sido arrastada. Medido: com a fila em 700px, filtrar mantinha 700px.
   *
   * Segundo passe no quadro seguinte porque o browser ainda reposiciona o
   * scroller depois do layout.
   */
  useLayoutEffect(() => {
    const track = trackRef.current
    if (!track) return

    track.scrollLeft = 0
    const frame = requestAnimationFrame(() => {
      track.scrollLeft = 0
    })
    return () => cancelAnimationFrame(frame)
  }, [resetKey])

  const syncEdges = useCallback(() => {
    const track = trackRef.current
    if (!track) return

    setAtStart(track.scrollLeft <= 1)
    setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 1)
  }, [])

  useEffect(() => {
    syncEdges()
    const track = trackRef.current
    if (!track) return

    const observer = new ResizeObserver(syncEdges)
    observer.observe(track)
    return () => observer.disconnect()
  }, [syncEdges])

  const scrollBy = (direction: 1 | -1) =>
    trackRef.current?.scrollBy({
      left: direction * Math.max(200, trackRef.current.clientWidth * 0.7),
      behavior: 'smooth',
    })

  return (
    <div className="flex min-w-0 items-center gap-2">
      <div
        id={id}
        ref={trackRef}
        role="radiogroup"
        aria-label={label}
        onScroll={syncEdges}
        // No desktop a roda do mouse só rola na vertical, e a barra está escondida:
        // sem converter o movimento, a fila simplesmente não anda. No trackpad o
        // gesto horizontal já vem em deltaX e passa direto.
        onWheel={(event) => {
          if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
          event.currentTarget.scrollLeft += event.deltaY
        }}
        className={cn(
          // min-w-0: sem ele o item flex herda min-width:auto, não encolhe abaixo
          // do conteúdo e a fila cresce além da largura em vez de rolar.
          'flex min-w-0 flex-1 gap-2 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [touch-action:pan-x] [&::-webkit-scrollbar]:hidden',
          // Sangra até as bordas da tela: a margem negativa cancela o padding do
          // container do header e o padding igual devolve o alinhamento, então o
          // primeiro chip continua na mesma coluna da logo e os do meio passam
          // rente à borda em vez de morrer num corte a 20px dela. Os valores
          // espelham o PUBLIC_GUTTER (px-5 sm:px-6 lg:px-10).
          '-mx-5 px-5 sm:-mx-6 sm:px-6',
          // No desktop volta para dentro do container: lá as setas ficam nas
          // pontas da faixa, e sangrar jogaria elas contra a borda da janela.
          'lg:mx-0 lg:px-0',
        )}
      >
        {children}
      </div>

      {/* DEPOIS do trilho, não sobre ele. Sobrepostas, a seta desabilitada da
          esquerda cobria o chip "Todos" — e no mobile, onde a fila sangra até as
          bordas, cobriria o primeiro chip sempre.

          Só no desktop: no toque o arrasto já resolve, e no mobile a linha é
          estreita demais para gastar 80px com dois botões. */}
      <div className="hidden shrink-0 items-center gap-2 lg:flex">
        <RailArrow
          direction="prev"
          disabled={atStart}
          controls={id}
          noun={noun}
          onClick={() => scrollBy(-1)}
        />
        <RailArrow
          direction="next"
          disabled={atEnd}
          controls={id}
          noun={noun}
          onClick={() => scrollBy(1)}
        />
      </div>
    </div>
  )
}
